import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ChartPie, CreditCard, CurrencyDollar, ArrowCounterClockwise, Receipt, Clock, FilePdf, FileXls, TrendUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function POSCashRegister() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = async () => {
    setLoading(true);
    try {
      const [salesRes, returnsRes, pmRes, productsRes] = await Promise.all([
        axios.get(`${API}/pos/sales?limit=1000`, { headers }),
        axios.get(`${API}/pos/returns`, { headers }),
        axios.get(`${API}/pos/payment-methods`, { headers }),
        axios.get(`${API}/products`, { headers }),
      ]);
      const pmMap = {};
      (pmRes.data || []).forEach(m => { pmMap[m.id] = m.name; });

      const productCosts = {};
      ((productsRes.data?.products || productsRes.data || [])).forEach(p => { productCosts[p.id] = p.costo || 0; });

      const daySales = (salesRes.data || []).filter(s => (s.created_at || "").startsWith(selectedDate));
      const dayReturns = (returnsRes.data || []).filter(r => (r.created_at || "").startsWith(selectedDate));

      const activeSales = daySales.filter(s => s.status !== "devuelta");
      const returnedSales = daySales.filter(s => s.status === "devuelta" || s.status === "parcial_devuelta");
      const creditSales = daySales.filter(s => s.status === "pendiente" && !s.paid_via);
      const mpPaidSales = daySales.filter(s => s.paid_via === "mercadopago");

      // By payment method
      const byMethod = {};
      activeSales.forEach(s => {
        const saleTotal = s.total || 0;
        (s.payments || []).forEach(p => {
          const name = pmMap[p.method] || p.method;
          byMethod[name] = (byMethod[name] || 0) + Math.min(p.amount || 0, saleTotal);
        });
      });

      // By doc type
      const typeLabels = { nota_venta: "Prefactura", remision: "Prefactura", prefactura: "Prefactura", factura: "Prefactura", cotizacion: "Cotizacion" };
      const byType = {};
      activeSales.forEach(s => {
        const label = typeLabels[s.doc_type] || s.doc_type;
        if (!byType[label]) byType[label] = { count: 0, total: 0 };
        byType[label].count += 1;
        byType[label].total += s.total || 0;
      });

      // Calculate profit
      let totalProfit = 0;
      activeSales.forEach(s => {
        (s.items || []).forEach(item => {
          const cost = productCosts[item.product_id] || 0;
          totalProfit += ((item.unit_price || 0) - cost) * (item.quantity || 0);
        });
      });

      setData({
        totalSales: activeSales.reduce((s, v) => s + (v.total || 0), 0),
        salesCount: activeSales.length,
        totalReturns: dayReturns.reduce((s, r) => s + (r.total_return || 0), 0),
        returnsCount: dayReturns.length,
        returnedSalesCount: returnedSales.length,
        creditTotal: creditSales.reduce((s, v) => s + (v.total || 0), 0),
        creditCount: creditSales.length,
        mpPaidTotal: mpPaidSales.reduce((s, v) => s + (v.total || 0), 0),
        mpPaidCount: mpPaidSales.length,
        totalProfit: Math.round(totalProfit * 100) / 100,
        byMethod,
        byType,
        sales: daySales,
        returns: dayReturns,
        pmMap,
      });
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [token, selectedDate]);

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/pos/resumen-dia/excel?date=${selectedDate}`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `resumen_ventas_${selectedDate}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar Excel"); }
  };

  const handleExportPdf = async () => {
    try {
      const res = await axios.get(`${API}/pos/resumen-dia/pdf?date=${selectedDate}`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `resumen_ventas_${selectedDate}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF descargado");
    } catch { toast.error("Error al exportar PDF"); }
  };

  const fmtMoney = (v) => `$${(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const DOC_LABELS = { nota_venta: "Prefactura", remision: "Prefactura", prefactura: "Prefactura", factura: "Prefactura" };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="pos-cash-register">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ChartPie size={24} weight="duotone" className="text-blue-600" /> Resumen del Dia</h1>
          <p className="text-sm text-slate-500">Ventas, devoluciones y desglose del dia</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            className="h-9 px-3 border rounded-lg text-sm" data-testid="day-selector" />
          <Button variant="outline" size="sm" className="h-9 text-xs text-red-700 border-red-200 hover:bg-red-50" onClick={handleExportPdf} data-testid="export-pdf">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={handleExportExcel} data-testid="export-excel">
            <FileXls size={14} className="mr-1" /> Excel
          </Button>
        </div>
      </div>

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-[10px] text-green-600 uppercase font-semibold">Ventas</p>
              <p className="text-2xl font-extrabold text-green-800 font-mono">{fmtMoney(data.totalSales)}</p>
              <p className="text-[10px] text-green-500">{data.salesCount} ventas</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-[10px] text-blue-600 uppercase font-semibold">Pagos en Linea</p>
              <p className="text-2xl font-extrabold text-blue-800 font-mono">{fmtMoney(data.mpPaidTotal)}</p>
              <p className="text-[10px] text-blue-500">{data.mpPaidCount} pagos MP</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-[10px] text-red-600 uppercase font-semibold">Devoluciones</p>
              <p className="text-2xl font-extrabold text-red-800 font-mono">{fmtMoney(data.totalReturns)}</p>
              <p className="text-[10px] text-red-500">{data.returnsCount} devoluciones</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-[10px] text-amber-600 uppercase font-semibold">Cuentas x Cobrar</p>
              <p className="text-2xl font-extrabold text-amber-800 font-mono">{fmtMoney(data.creditTotal)}</p>
              <p className="text-[10px] text-amber-500">{data.creditCount} pendientes</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-[10px] text-emerald-600 uppercase font-semibold">Neto</p>
              <p className="text-2xl font-extrabold text-emerald-800 font-mono">{fmtMoney(data.totalSales - data.totalReturns)}</p>
              <p className="text-[10px] text-emerald-500">Ventas - Devoluciones</p>
            </div>
            {isAdmin && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <p className="text-[10px] text-purple-600 uppercase font-semibold flex items-center gap-1"><TrendUp size={10} /> Utilidad</p>
              <p className="text-2xl font-extrabold text-purple-800 font-mono">{fmtMoney(data.totalProfit)}</p>
              <p className="text-[10px] text-purple-500">Precio - Costo</p>
            </div>
            )}
          </div>

          {/* By Method + By Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border rounded-xl p-4">
              <h4 className="text-xs font-bold text-slate-600 uppercase mb-3 flex items-center gap-1.5"><CreditCard size={14} /> Por Metodo de Pago</h4>
              {Object.keys(data.byMethod).length === 0 ? (
                <p className="text-xs text-slate-400">Sin ventas</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data.byMethod).map(([method, amount]) => (
                    <div key={method} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-xs font-medium text-slate-700 capitalize">{method}</span>
                      <span className="text-xs font-mono font-bold text-slate-900">{fmtMoney(amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border rounded-xl p-4">
              <h4 className="text-xs font-bold text-slate-600 uppercase mb-3 flex items-center gap-1.5"><CurrencyDollar size={14} /> Por Tipo de Documento</h4>
              {Object.keys(data.byType).length === 0 ? (
                <p className="text-xs text-slate-400">Sin ventas</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data.byType).map(([type, d]) => (
                    <div key={type} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
                      <div>
                        <span className="text-xs font-medium text-slate-700">{type}</span>
                        <span className="text-[10px] text-slate-400 ml-1">({d.count})</span>
                      </div>
                      <span className="text-xs font-mono font-bold text-slate-900">{fmtMoney(d.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sales List */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h3 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5"><Receipt size={14} /> Ventas del Dia ({data.sales.length})</h3>
            </div>
            {data.sales.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Sin ventas en esta fecha</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left bg-slate-50">
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Folio</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Hora</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Cliente</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Tipo</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Pago</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Status</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sales.map(s => {
                    const isRet = s.status === "devuelta" || s.status === "parcial_devuelta";
                    return (
                      <tr key={s.id} className={`border-b ${isRet ? "bg-red-50" : "bg-green-50/30"}`}>
                        <td className={`px-3 py-2 text-xs font-mono font-bold ${isRet ? "text-red-600" : "text-green-700"}`}>{s.folio}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{(s.created_at || "").slice(11, 16)}</td>
                        <td className={`px-3 py-2 text-xs ${isRet ? "text-red-500 line-through" : "text-slate-800"}`}>{s.customer_name}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="text-[9px] font-bold text-slate-500">{DOC_LABELS[s.doc_type] || s.doc_type}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{(s.payments || []).map(p => data.pmMap[p.method] || p.method).join(", ")}</td>
                        <td className="px-3 py-2 text-center">
                          {isRet ? (
                            <span className="text-[9px] font-bold bg-red-600 text-white px-2 py-0.5 rounded">{s.status === "devuelta" ? "DEVUELTA" : "PARCIAL"}</span>
                          ) : s.paid_via === "mercadopago" ? (
                            <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">PAGADO MP</span>
                          ) : s.status === "pendiente" ? (
                            <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">POR COBRAR</span>
                          ) : (
                            <span className="text-[9px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">PAGADA</span>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${isRet ? "text-red-600 line-through" : "text-slate-900"}`}>{fmtMoney(s.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Returns List */}
          {data.returns.length > 0 && (
            <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b bg-red-50">
                <h3 className="text-xs font-bold text-red-700 uppercase flex items-center gap-1.5"><ArrowCounterClockwise size={14} /> Devoluciones del Dia ({data.returns.length})</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Folio Dev.</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Venta</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Cliente</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Motivo</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {data.returns.map(r => (
                    <tr key={r.id} className="border-b bg-red-50/50">
                      <td className="px-3 py-2 text-xs font-mono font-bold text-red-600">{r.folio}</td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.sale_folio}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{r.customer_name}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{r.reason || "-"}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-red-600">{fmtMoney(r.total_return)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
